import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Clock, Star, Package, Wallet, TrendingUp } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { CHEFS, DISHES } from "@/lib/mockData";

const STATS = [
  { icon: Package, label: "Orders", value: "24", accent: "#d00000" },
  { icon: Heart, label: "Following", value: "8", accent: "#d00000" },
  { icon: Star, label: "Reviews left", value: "16", accent: "#d00000" },
  { icon: Wallet, label: "Jollanda credit", value: "€32", accent: "#d00000" },
];

const ORDERS = [
  { id: "#JL-1042", dish: DISHES[0], status: "Delivered", date: "Today" },
  { id: "#JL-1039", dish: DISHES[2], status: "On the way", date: "Yesterday" },
  { id: "#JL-1021", dish: DISHES[1], status: "Delivered", date: "3 days ago" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const following = CHEFS.slice(0, 3);

  return (
    <div data-testid="dashboard-page" className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <p className="text-xs uppercase tracking-[0.25em] text-[#d00000] mb-2">Your kitchen pass</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-neutral-900">
          Welcome back, {user?.name?.split(" ")[0] || "friend"}.
        </h1>
        <p className="mt-3 text-neutral-500">{user?.email}</p>
      </motion.div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
        {STATS.map((s, i) => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
            className="bg-white border border-neutral-200 p-6" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
            <s.icon className="w-6 h-6 mb-4" style={{ color: s.accent }} />
            <p className="font-display text-4xl font-black text-neutral-900">{s.value}</p>
            <p className="text-sm text-neutral-500 mt-1">{s.label}</p>
          </motion.div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-8 mt-12">
        {/* Orders */}
        <div className="lg:col-span-2">
          <h2 className="font-display text-2xl font-bold text-neutral-900 mb-6">Recent orders</h2>
          <div className="border border-neutral-200 divide-y divide-neutral-200">
            {ORDERS.map((o) => (
              <div key={o.id} className="flex items-center gap-4 p-4 bg-white" data-testid={`order-${o.id.replace(/[#-]/g, "")}`}>
                <img src={o.dish.img} alt="" className="w-16 h-16 object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="text-neutral-900 font-medium truncate">{o.dish.name}</p>
                  <p className="text-sm text-neutral-400">{o.id} · {o.date}</p>
                </div>
                <span className={`text-xs px-3 py-1 whitespace-nowrap ${o.status === "Delivered" ? "bg-neutral-100 text-neutral-600" : "bg-[#f5a623]/15 text-[#b8791a]"}`}>
                  {o.status === "On the way" && <Clock className="w-3 h-3 inline mr-1" />}{o.status}
                </span>
                <span className="text-neutral-900 font-semibold hidden sm:block">€{o.dish.price}</span>
              </div>
            ))}
          </div>

          <h2 className="font-display text-2xl font-bold text-neutral-900 mb-6 mt-12">Recommended for you</h2>
          <div className="grid grid-cols-3 gap-4">
            {DISHES.slice(4, 7).map((d) => (
              <div key={d.id} className="border border-neutral-200 overflow-hidden group" data-testid={`rec-${d.id}`}>
                <div className="aspect-square overflow-hidden">
                  <img src={d.img} alt={d.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-3">
                  <p className="text-sm text-neutral-900 truncate">{d.name}</p>
                  <p className="text-xs text-[#d00000]">€{d.price}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Following */}
        <div>
          <h2 className="font-display text-2xl font-bold text-neutral-900 mb-6 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#d00000]" /> Chefs you follow
          </h2>
          <div className="space-y-3">
            {following.map((c) => (
              <Link key={c.id} to={`/chef/${c.id}`} className="flex items-center gap-3 p-3 bg-white border border-neutral-200 hover:border-[#d00000] transition-colors" data-testid={`following-${c.id}`}>
                <img src={c.img} alt={c.name} className="w-12 h-12 object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="text-neutral-900 text-sm font-medium truncate">{c.name}</p>
                  <p className="text-xs text-neutral-400">{c.cuisine}</p>
                </div>
                <span className="flex items-center gap-1 text-xs text-neutral-900"><Star className="w-3 h-3 fill-[#f5a623] text-[#f5a623]" /> {c.rating}</span>
              </Link>
            ))}
          </div>
          <div className="bg-[#d00000] p-6 mt-6">
            <p className="font-display text-xl font-bold text-white">Cook & earn</p>
            <p className="text-white/80 text-sm mt-2">Ready to sell your own dishes? Apply to become a Jollanda chef.</p>
            <Link to="/partners"><button className="mt-4 bg-white text-[#d00000] text-sm px-4 py-2 font-medium" data-testid="dashboard-become-chef">Learn more →</button></Link>
          </div>
        </div>
      </div>
    </div>
  );
}
